import { type PaletteKey, type PixelCanvas, sprite } from '../../../lib';
import { box, geometry, outlined, round, taper } from '../effect-guns.sprite';

/**
 * Pepper Mill (the Chef set's gun, ADR-0160 amended): a big turned-wood pepper mill held level. At its back a brass
 * crown (a knob of ridges round its axis), a neck under the fist, a bellied body of polished wood with a red chilli band
 * and a brass collar, a waist, a brass grinder ring flaring at its foot and a dark iron burr narrowing to the muzzle,
 * embers glowing in it. On a shot (`fire`) the crown turns (its ridges roll round), the burr flashes, then it settles.
 */

const WOOD = ['N', 'n', 'm', 't'] as const;
const BRASS = ['U', 'Y', 'Y', 'y'] as const;

/** The body's half-height at x: shoulders rising off the neck, a long barrel with a soft waist, shoulders down to its foot. */
const smooth = (t: number) => (t <= 0 ? 0 : t >= 1 ? 1 : t * t * (3 - 2 * t));
const belly = (x: number) =>
  3.5 + 3.5 * smooth((x - 13) / 7) - 0.6 * smooth(1 - Math.abs(x - 31) / 6) - 2.5 * smooth((x - 40) / 5);

/** The belly's tone at x, `top` rows from its top and `bottom` from its foot: a polished cylinder lit from above. */
function bellyTone(x: number, top: number, bottom: number): PaletteKey {
  const r = top / (top + bottom || 1);
  if (x >= 37 && x <= 38) return r < 0.15 ? 'i' : r > 0.8 ? 'R' : 'r';
  if (x >= 43) return r < 0.15 ? 'y' : r > 0.75 ? 'U' : 'Y';
  if (top === 0) return 't';
  if (r < 0.3) return top === 1 && x > 17 && x < 34 ? 'e' : 't';
  if (r < 0.62) return (x * 3 + top * 5) % 11 === 0 ? 'n' : 'm';
  return r < 0.86 || bottom > 0 ? 'n' : 'N';
}

const mill = (turn: number, heat = 0) =>
  outlined((c: PixelCanvas) => {
    // The crown: a brass knob, its ridges running round the axis, rolling by as it turns; a ball finial behind it.
    taper(c, 0, 1, 20, 1.5, 2, round(BRASS));
    taper(c, 2, 9, 20, 5, 5.5, (top, bottom, x, y): PaletteKey | null => {
      // Its crown's points along its top and foot.
      if ((top === 0 || bottom === 0) && x % 2 === 1) return null;
      if (top <= 1) return 'y';
      if (bottom === 0) return 'U';
      if ((y + turn) % 3 === 0) return 'U';
      return x === 3 ? 'y' : y < 20 ? 'Y' : 'Y';
    });
    // The neck under the fist.
    taper(c, 10, 13, 20, 3, 3.5, round(WOOD));
    // The belly: polished wood, a long highlight down its top, a red chilli band round it, a brass collar; each
    // column filled to its own half-height (its curve).
    for (let x = 13; x <= 45; x++) {
      const h = Math.round(belly(x));
      for (let y = 20 - h; y <= 20 + h; y++) c.set(x, y, bellyTone(x, y - (20 - h), 20 + h - y));
    }
    // The waist and the grinder's brass ring flaring at its foot.
    taper(c, 46, 50, 20, 4, 4.5, round(WOOD));
    taper(c, 51, 56, 20, 5.5, 7, (top, bottom, x): PaletteKey => (x === 51 ? 'U' : round(BRASS)(top, bottom)));
    // The iron burr narrowing to the muzzle, embers in its mouth (white-hot on a shot).
    taper(c, 57, 63, 20, 4, 2, (top, bottom, x): PaletteKey => {
      if (x >= 62) return heat >= 2 ? 'w' : heat === 1 ? 'y' : turn % 2 ? 'o' : 'u';
      return top === 0 ? '3' : bottom === 0 ? 'k' : '2';
    });
    // The trigger: a little brass key under the neck.
    box(c, 11, 25, 13, 27, [0, 1], (_x, y): PaletteKey => (y === 25 ? 'Y' : 'U'), 'r');
  });

export default sprite({
  name: 'pollinator_chef',
  ...geometry,
  notes: `Pepper Mill (ADR-0160 amended, the Chef set's gun): a turned-wood pepper mill held level. A brass crown at its
back (ridges round its axis) behind a ball finial, a wooden neck under the fist, a bellied body of polished wood (a cream
highlight down its top) with a red chilli band and a brass collar, a waist, a brass grinder ring flaring at its foot and
a dark iron burr narrowing to the muzzle, embers glowing in its mouth (2 frames). Fire: the crown's ridges roll round as
it turns, the burr's mouth flashing white then gold, over 4 frames. Its flair: the embers' glow, flecks of chilli falling
from it. No team keys. Rubric (4×): S4 R4 P4 L4 C4 A4 F4.`,
  anims: {
    idle: { fps: 3, frames: [mill(0), mill(1)] },
    fire: { fps: 20, loop: false, frames: [mill(1, 2), mill(2, 2), mill(0, 1), mill(1)] },
  },
});
